/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useState } from "react"
import { useDispatch } from "react-redux"
import { BaseDepartment, Department } from "types/models/menu"
import { SortableResource } from "store/duck/menu.type"
import { TroyWebTracker } from "utils/tracking"
import { EventAction, EventCategory } from "constants/tracking"
import { sortMenuItemNewLogicWithSortedIndexes } from "utils/sort_helper"
import {
  fetchSortDepartments,
  FetchSortDepartmentsOptions,
} from "utils/rtk_migration_helper"
import { useLazyFetchMenuQuery, useSortDepartmentsMutation } from "queries"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"

// Shared setup for the department / sub-department sort drawers: both sort through
// the same sort-departments endpoint and share the RTK migration flag handling.
export const useSortDepartmentsDrawerSetup = () => {
  const dispatch = useDispatch()
  const [sortedIndexes, setSortedIndexes] = useState<[number, number][]>([])
  const [sortDepartmentsRtk] = useSortDepartmentsMutation()
  const {
    flags: {
      sortDepartments: isRtkSortDepartmentsEnabled,
      fetchMenu: isRtkFetchMenuEnabled,
    },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()
  const [fetchMenuRtk] = useLazyFetchMenuQuery()

  return {
    dispatch,
    sortedIndexes,
    setSortedIndexes,
    sortDepartmentsRtk,
    isRtkSortDepartmentsEnabled,
    isRtkFetchMenuEnabled,
    isRtkFlagLoading,
    fetchMenuRtk,
  }
}

interface SortDrawerTrackingOptions {
  category: EventCategory
  action: EventAction
  merchantID: string
  merchantGroupID: string
  menuGroupID: string
  // drawer-specific label: SECTION_ID for departments, CATEGORY_ID for sub-departments
  contextLabels: { SECTION_ID?: string } | { CATEGORY_ID?: string }
  enablePartialMenu: boolean
}

export const sendSortDrawerTrackingEvent = (
  options: SortDrawerTrackingOptions,
) => {
  TroyWebTracker.sendEvent({
    category: options.category,
    action: options.action,
    labels: {
      ...(options.merchantID
        ? { STORE_ID: options.merchantID }
        : {
            MERCHANT_GROUP_ID: options.merchantGroupID,
            MENU_GROUP_ID: options.menuGroupID,
          }),
      ...options.contextLabels,
      IS_PARTIAL_MENU: options.enablePartialMenu,
    },
  })
}

export interface SubmitSortDepartmentsOptions
  extends Omit<FetchSortDepartmentsOptions, "sorts"> {
  // items in their pre-drag order, used to look up existing sort orders
  originalItems: BaseDepartment[]
  // items in the order shown when the drawer was saved
  sortedItems: Department[]
  sortedIndexes: number[][]
  isUCS: boolean
  isRtkFlagLoading: boolean
}

export const submitSortDepartments = (
  options: SubmitSortDepartmentsOptions,
) => {
  const {
    originalItems,
    sortedItems,
    sortedIndexes,
    isUCS,
    isRtkFlagLoading,
    ...fetchOptions
  } = options

  let sorts: SortableResource[]
  if (fetchOptions.enablePartialMenu) {
    sorts = sortedItems.map((department, index) => ({
      resourceID: department.itemClassID ?? "",
      sortOrder: originalItems[index].sortOrder ?? 0,
    }))
  } else if (isUCS) {
    sorts = sortMenuItemNewLogicWithSortedIndexes({
      originalList: originalItems,
      sortedIndexes,
      sortKey: "sortOrder",
      itemKey: "itemClassID",
    })
  } else {
    sorts = sortedItems.map((department, index) => ({
      resourceID: department.itemClassID ?? "",
      sortOrder: sortedItems.length - index,
    }))
  }

  // While migration flags are loading, do not fall back to old Redux: the enabled bit is
  // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
  // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
  // the trigger UI while loading so clicks are not silently dropped.
  if (!isRtkFlagLoading) {
    fetchSortDepartments({
      ...fetchOptions,
      sorts,
    })
  }
}
